import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {useIntlEx} from "./IntlUtils";
import {useSolv} from "./SolvProvider";
import {ActionButton} from "./FormComps";

export function StripeControls({ options, createOrder, onApprove, onCancel, onClick }) {

  const {intl} = useIntlEx()

  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);

  // Initialize Stripe using the provided publishable key (options.clientId)
  useEffect(() => {
    if (options?.clientId) {
      setStripePromise(loadStripe(options.clientId));
    }
  }, [options?.clientId]);

  const handleInitialClick = async () => {
    if (onClick) onClick();
    setLoading(true);

    try {
      // createOrder must return the client_secret generated by your backend
      const secret = await createOrder();
      if (secret) {
        setClientSecret(secret);
      }
    }
    catch (error) {
      console.error("StripeButton createOrder Error:", error);
    }
    finally {
      setLoading(false);
    }
  };

  // If we have the client secret, show the actual Stripe input fields
  if (clientSecret) {
    return (
      <Elements stripe={stripePromise} options={{
        clientSecret,
        appearance: {
          theme: "stripe",
        }
      }}>
        <StripeCheckoutForm
          onApprove={onApprove}
          onCancel={() => {
            setClientSecret(null);
            if (onCancel) onCancel();
          }}
        />
      </Elements>
    );
  }

  // Otherwise, show the trigger button
  return (
    <ActionButton className="btn-blue btn-payment" msg="pay_with_stripe" icon="credit_card" iconProps={{width: "2em", height: "2em"}} disabled={loading} onClick={handleInitialClick}/>
  );
}

// Internal component to handle the actual Stripe form submission
function StripeCheckoutForm({ onApprove, onCancel }) {

  const {session} = useSolv()
  const stripe = useStripe();
  const elements = useElements();

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    // confirmPayment processes the card details using the client_secret
    const result = await stripe.confirmPayment({
      elements,
      redirect: "if_required", // Prevents automatic redirect so we can handle success locally
    });

    if (result.error) {
      setErrorMessage(result.error.message);
      setIsProcessing(false);
    }
    else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
      // Pass the payment details back to your main component
      onApprove(result.paymentIntent);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ border: '1px solid #ccc', padding: '16px', borderRadius: '8px', marginBottom: '10px' }}>
      <PaymentElement options={{
        layout: {
          type: "accordion",
          defaultCollapsed: false,
        },
        defaultValues: {
          billingDetails: {
            name: session?.user?.fullName || "",
            email: session?.user?.emailAddress || '',
          }
        }
      }}/>
      {errorMessage && <div style={{ color: 'red', marginTop: '10px' }}>{errorMessage}</div>}

      <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isProcessing}>
          Cancel
        </button>
        <button type="submit" className="btn btn-blue" disabled={isProcessing || !stripe || !elements}>
          {isProcessing ? "Processing..." : "Confirm Payment"}
        </button>
      </div>
    </form>
  );
}